import type {
  CreateHelpArticleRequest,
  CreateHelpCategoryRequest,
  HelpArticle,
  HelpCategory,
  UpdateHelpArticleRequest,
  UpdateHelpCategoryRequest,
} from '@nucleus-commerce/api-client/admin';

import { emptySeoOverride, fromSeoOverride, toSeoOverride } from '@/features/seo';

import type {
  HelpArticleFormInput,
  HelpArticleFormOutput,
} from '../schemas/help-article-form-schema';
import type { HelpCategoryForm } from '../schemas/help-category-form-schema';

const orNull = (value: string): string | null => (value.trim() === '' ? null : value);
const positionOrNull = (value: string): number | null =>
  value.trim() === '' ? null : Number(value.trim());

export function emptyHelpArticleForm(): HelpArticleFormInput {
  return {
    title: '',
    handle: '',
    excerpt: '',
    bodyHtml: '',
    categoryId: '',
    keywords: [],
    locale: '',
    position: '',
    status: 'draft',
    visibility: 'public',
    seo: emptySeoOverride(),
  };
}

export function fromHelpArticle(article: HelpArticle): HelpArticleFormInput {
  return {
    title: article.title,
    handle: article.handle,
    excerpt: article.excerpt ?? '',
    bodyHtml: article.bodyHtml ?? '',
    categoryId: article.category?.id ?? '',
    keywords: [...article.keywords],
    locale: article.locale ?? '',
    position: String(article.position),
    status: article.status,
    visibility: article.visibility,
    seo: fromSeoOverride(article.seo),
  };
}

/** Every field as the API wants it; create and update both start from here. */
function fullBody(values: HelpArticleFormInput) {
  return {
    handle: values.handle.trim(),
    title: values.title.trim(),
    excerpt: orNull(values.excerpt.trim()),
    bodyHtml: orNull(values.bodyHtml),
    categoryId: orNull(values.categoryId),
    keywords: values.keywords.map((keyword) => keyword.trim()),
    locale: orNull(values.locale),
    position: positionOrNull(values.position),
    status: values.status,
    visibility: values.visibility,
    seo: toSeoOverride(values.seo),
  };
}

/** Empty optional fields are left out; the backend defaults them. */
export function toHelpArticleCreate(values: HelpArticleFormOutput): CreateHelpArticleRequest {
  const body = fullBody(values);
  return {
    handle: body.handle,
    title: body.title,
    status: body.status,
    visibility: body.visibility,
    ...(body.excerpt !== null ? { excerpt: body.excerpt } : {}),
    ...(body.bodyHtml !== null ? { bodyHtml: body.bodyHtml } : {}),
    ...(body.categoryId !== null ? { categoryId: body.categoryId } : {}),
    ...(body.keywords.length > 0 ? { keywords: body.keywords } : {}),
    ...(body.locale !== null ? { locale: body.locale } : {}),
    ...(body.position !== null ? { position: body.position } : {}),
    ...(body.seo !== null ? { seo: body.seo } : {}),
  };
}

/**
 * Only what changed against the loaded article travels, so an untouched handle never triggers a
 * redirect. A cleared position is not sent: the article keeps its place.
 */
export function toHelpArticleUpdate(
  values: HelpArticleFormOutput,
  initial: HelpArticleFormInput,
): UpdateHelpArticleRequest {
  const next = fullBody(values);
  const before = fullBody(initial);
  const body: Record<string, unknown> = {};
  for (const key of Object.keys(next) as Array<keyof typeof next>) {
    if (key === 'position' && next.position === null) continue;
    if (JSON.stringify(next[key]) !== JSON.stringify(before[key])) body[key] = next[key];
  }
  return body;
}

export function emptyHelpCategoryForm(nextPosition: number): HelpCategoryForm {
  return {
    name: '',
    handle: '',
    description: '',
    position: String(nextPosition),
    visibility: 'public',
  };
}

export function fromHelpCategory(category: HelpCategory): HelpCategoryForm {
  return {
    name: category.name,
    handle: category.handle,
    description: category.description ?? '',
    position: String(category.position),
    visibility: category.visibility,
  };
}

export function toHelpCategoryCreate(values: HelpCategoryForm): CreateHelpCategoryRequest {
  const description = values.description.trim();
  const position = values.position.trim();
  return {
    handle: values.handle.trim(),
    name: values.name.trim(),
    visibility: values.visibility,
    ...(description ? { description } : {}),
    ...(position ? { position: Number(position) } : {}),
  };
}

export function toHelpCategoryUpdate(
  values: HelpCategoryForm,
  initial: HelpCategoryForm,
): UpdateHelpCategoryRequest {
  const body: UpdateHelpCategoryRequest = {};
  if (values.handle.trim() !== initial.handle) body.handle = values.handle.trim();
  if (values.name.trim() !== initial.name) body.name = values.name.trim();
  if (values.description.trim() !== initial.description) {
    body.description = orNull(values.description.trim());
  }
  const position = values.position.trim();
  if (position !== '' && position !== initial.position) body.position = Number(position);
  if (values.visibility !== initial.visibility) body.visibility = values.visibility;
  return body;
}
